import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { FieldChange } from '../types';
import { formatValue } from '../utils';

interface SideBySideDiffProps {
  changes: FieldChange[];
}

/** Tints a changed field by what happened to it. */
const CHANGE_TINT: Record<FieldChange['changeType'], string> = {
  added: 'success.main',
  removed: 'error.main',
  modified: 'warning.main',
};

const Panel: React.FC<{ title: string; tone: 'error' | 'success'; children: React.ReactNode }> = ({
  title,
  tone,
  children,
}) => (
  <Box sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', minWidth: 0 }}>
    <Box
      sx={(theme) => ({
        px: 2,
        py: 1,
        fontWeight: 'bold',
        borderBottom: 1,
        borderColor: 'divider',
        // A tint rather than the solid palette colour, so the heading text stays readable
        // in both colour schemes.
        bgcolor: alpha(theme.palette[tone].main, 0.15),
        color: `${tone}.main`,
      })}
    >
      {title}
    </Box>
    <Box sx={{ px: 2, py: 1, maxHeight: 300, overflowY: 'auto' }}>{children}</Box>
  </Box>
);

const Field: React.FC<{ change: FieldChange; children: React.ReactNode }> = ({ change, children }) => (
  <Box
    sx={{
      mb: 1,
      p: 0.5,
      borderRadius: 0.5,
      borderLeft: 3,
      borderColor: CHANGE_TINT[change.changeType],
    }}
  >
    <Typography component='span' sx={{ fontWeight: 600, mr: 1 }}>
      {change.fieldName}:
    </Typography>
    <Box
      component='pre'
      sx={{
        m: 0,
        p: 0.5,
        bgcolor: 'action.hover',
        borderRadius: 0.5,
        fontSize: '0.875rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {children}
    </Box>
  </Box>
);

export const SideBySideDiff: React.FC<SideBySideDiffProps> = ({ changes }) => {
  if (changes.length === 0) {
    return <Typography sx={{ color: 'text.secondary' }}>No changes detected</Typography>;
  }

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <Panel title='Previous Values' tone='error'>
        {changes.map((change, index) => (
          <Field key={index} change={change}>
            {change.changeType === 'added' ? (
              <Typography component='span' sx={{ color: 'text.secondary' }}>
                (not set)
              </Typography>
            ) : (
              formatValue(change.oldValue)
            )}
          </Field>
        ))}
      </Panel>
      <Panel title='Current Values' tone='success'>
        {changes.map((change, index) => (
          <Field key={index} change={change}>
            {change.changeType === 'removed' ? (
              <Typography component='span' sx={{ color: 'text.secondary' }}>
                (removed)
              </Typography>
            ) : (
              formatValue(change.newValue)
            )}
          </Field>
        ))}
      </Panel>
    </Stack>
  );
};
